import { create } from "zustand";

import type { MediaKind } from "../lib/api-client.js";
import type { PersistedMedia } from "../lib/storage/types.js";

export type MediaUploadStatus = "uploading" | "ready" | "error";

export interface MediaAttachment {
  /** Stable client-side id (UUID); not the Telegram file_id. */
  id: string;
  kind: MediaKind;
  /**
   * Source for inline preview rendering. During an active upload session
   * this is a `blob:` URL pointing at the picked File. After restore from
   * a persisted draft we populate it with `thumbDataUrl` (photos) or
   * leave it empty (video/audio) so the slot falls back to a kind-icon.
   */
  blobUrl: string;
  /** Original filename, used as a fallback when the bot sees no name. */
  name: string;
  mime: string;
  size: number;
  status: MediaUploadStatus;
  /** Telegram file_id once upload succeeds. */
  fileId?: string;
  /** Stable error code (from `UploadMediaError.code` or `network`/`unknown`). */
  errorCode?: string;
  /**
   * Tiny base64 thumbnail captured at upload time (photos only). Persisted
   * with the draft so the preview survives reopen even after the original
   * blob URL is gone.
   */
  thumbDataUrl?: string;
  /**
   * Full-quality data: URL of the picked photo, kept in memory for the
   * live editing session only. Used by the Telegram preview mosaic so
   * the user sees their actual image, not the downscaled thumb. Never
   * persisted — would blow CloudStorage's 1024-key per-user budget.
   * After draft reopen this is undefined and the preview falls back
   * to `thumbDataUrl`.
   */
  originalDataUrl?: string;
  /**
   * Visible only while the upload is in its retry-backoff window. Lets
   * the slot render "Retrying… 2/3" instead of looking idle, which is
   * what users were misreading as "retry doesn't work".
   */
  retryAttempt?: number;
}

interface MediaState {
  /**
   * The current attachment, if any. The Mini App's MVP keeps it to a single
   * media item per post; albums (sendMediaGroup) are a future extension.
   */
  attachment: MediaAttachment | null;

  setUploading: (a: Omit<MediaAttachment, "status">) => void;
  /**
   * Mark the upload as complete. The optional `thumbDataUrl` is the
   * server-rendered preview the bot returns for photos — used to
   * populate the slot when the client couldn't generate one locally
   * (Android Telegram WebView). If the local thumb is already set we
   * keep it (it was instant; the server thumb is just a fallback).
   */
  setReady: (id: string, fileId: string, thumbDataUrl?: string) => void;
  setError: (id: string, errorCode: string) => void;
  /**
   * Mark the in-flight upload as currently sleeping in retry backoff.
   * Cleared automatically by the next successful attempt or by setError.
   */
  setRetryAttempt: (id: string, attempt: number) => void;
  clear: () => void;
  /**
   * Hydrate the slot from a draft's persisted media. Sets status to
   * "ready" so the share controller treats it as already uploaded.
   * If `media` is null/undefined, the slot is cleared instead — call
   * this on every draft switch to ensure each draft owns its own slot.
   */
  restoreFromDraft: (media: PersistedMedia | null | undefined) => void;
}

export const useMediaStore = create<MediaState>((set) => ({
  attachment: null,

  setUploading: (a) => {
    set((state) => {
      // Revoke prior blob if we're replacing the slot.
      if (state.attachment && state.attachment.blobUrl !== a.blobUrl) {
        try {
          URL.revokeObjectURL(state.attachment.blobUrl);
        } catch {
          // ignore — best-effort cleanup
        }
      }
      return { attachment: { ...a, status: "uploading" } };
    });
  },

  setReady: (id, fileId, thumbDataUrl) => {
    set((state) => {
      if (!state.attachment || state.attachment.id !== id) return state;
      const next: MediaAttachment = {
        ...state.attachment,
        status: "ready",
        fileId,
      };
      delete next.retryAttempt;
      // Adopt the server thumb only if we don't already have a local
      // one — local thumbs are instant and avoid a needless re-paint.
      if (thumbDataUrl && !state.attachment.thumbDataUrl) {
        next.thumbDataUrl = thumbDataUrl;
        // The slot's blobUrl was an unusable blob: ref on Android.
        // Swap it for the server thumb so the slot's <img> renders.
        if (
          state.attachment.blobUrl.length === 0 ||
          state.attachment.blobUrl.startsWith("blob:")
        ) {
          next.blobUrl = thumbDataUrl;
        }
      }
      return { attachment: next };
    });
  },

  setError: (id, errorCode) => {
    set((state) => {
      if (!state.attachment || state.attachment.id !== id) return state;
      const next = {
        ...state.attachment,
        status: "error" as const,
        errorCode,
      };
      delete next.retryAttempt;
      return { attachment: next };
    });
  },

  setRetryAttempt: (id, attempt) => {
    set((state) => {
      if (!state.attachment || state.attachment.id !== id) return state;
      return {
        attachment: { ...state.attachment, retryAttempt: attempt },
      };
    });
  },

  clear: () => {
    set((state) => {
      if (state.attachment) {
        try {
          URL.revokeObjectURL(state.attachment.blobUrl);
        } catch {
          // ignore
        }
      }
      return { attachment: null };
    });
  },

  restoreFromDraft: (media) => {
    set((state) => {
      // Always revoke any prior blob — restore replaces the slot wholesale.
      if (state.attachment) {
        try {
          URL.revokeObjectURL(state.attachment.blobUrl);
        } catch {
          // ignore
        }
      }
      if (!media) {
        return { attachment: null };
      }
      return {
        attachment: {
          id: globalThis.crypto.randomUUID(),
          kind: media.kind,
          blobUrl: media.thumbDataUrl ?? "",
          name: media.name,
          mime: media.mime,
          size: media.size,
          status: "ready",
          fileId: media.fileId,
          ...(media.thumbDataUrl
            ? { thumbDataUrl: media.thumbDataUrl }
            : {}),
        },
      };
    });
  },
}));
